import './init'
import * as Sentry from '@sentry/react'
import { Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { forkJoin, from, of } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'
import pkg from '../package.json'
import App from './App'
import api from './api'
import { logout$ } from './api/brm/authentication'
import { ModalsProvider } from './hooks/useModals'
import { NotificationsProvider } from './hooks/useNotifications'
import { getInstallType, siteSetup } from './utils/site'
import userStore, { createSession$ } from './zustand/user'

const siteSettings = siteSetup()

if (getInstallType() === 'prod') {
  Sentry.init({
    dsn: 'https://08826e78e99049c6a1b4c02737ecd41d@o433246.ingest.sentry.io/5387982',
    ignoreErrors: [
      'Error: Faulty X-Revision value provided',
      'Error: Login token has expired!',
      'Error: No token found!',
      'Error: Provided token is expired.',
      'Error: Unauthorized.',
      'Error: User or password is wrong.',
      'ResizeObserver loop completed with undelivered notifications.',
      'ResizeObserver loop limit exceeded',
      `SyntaxError: Unexpected token '<'`,
      `SyntaxError: expected expression, got '<'`,
      'TypeError: Abgebrochen',
      'TypeError: Cancelled',
      'TypeError: Die Netzwerkverbindung wurde unterbrochen.',
      'TypeError: Failed to fetch',
      'TypeError: NetworkError when attempting to fetch resource.'
    ],
    release: `${pkg.version}${
      import.meta.env.REACT_APP_CURRENT_REV
        ? `@${import.meta.env.REACT_APP_CURRENT_REV}`
        : ''
    }`
  })
}

const renderApp = () => {
  const container = window.document.getElementById('root')
  const root = createRoot(container)

  root.render(
    <Suspense
      fallback={
        <div
          style={{ color: 'lightgray', fontFamily: 'monospace', padding: 16 }}
        >
          Initializing…
        </div>
      }
    >
      <ModalsProvider>
        <NotificationsProvider>
          <App />
        </NotificationsProvider>
      </ModalsProvider>
    </Suspense>
  )
}

const init = () =>
  from(window.caches?.keys() || of([]))
    .pipe(
      switchMap((cacheNames) =>
        // Try to authenticate and purge api cache
        forkJoin([
          api.authentication.authenticate$(),
          Promise.all(
            cacheNames.map((cacheName) => window.caches.delete(cacheName))
          )
        ])
      ),
      catchError((error) => {
        // Catch cache erros
        console.debug(error.message)
        return of([])
      }),
      switchMap(([auth]) => {
        if (auth?.user) {
          return createSession$(auth.user)
        }

        // Just emit empty response if no session was established
        return of([])
      })
    )
    .subscribe(
      ([nextUser]) => {
        if (nextUser) {
          userStore.setState({ user: nextUser })
        }

        // Initialize app with appropriate theme
        if (siteSettings.cssTag) {
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = `/static/${siteSettings.cssTag}.css`
          document.head.appendChild(link)
          document.body.classList.add(siteSettings.cssTag)
        }
        renderApp()
      },
      (error) => {
        // Catch and display init errors
        console.error(error)

        // Remove cookie so user doesn't get stuck in broken session
        logout$().subscribe(() => {}, console.error)

        const el = document.createElement('div')
        el.classList.add('p-3', 'text-black-50', 'text-monospace')
        el.textContent = `Error during initialization: ${error.message}`
        document.body.append(el)
      }
    )

init()
